import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2,
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AddressAutocompleteService } from "../../services/address-autocomplete.service";
import { IAddressValue } from "../../interfaces/address-value.interface";
import { CustomInput } from "../../../../../classes/custom-input";
import { TranslatePipe } from "@ngx-translate/core";
import { FormErrorMessageComponent } from "@app/kernel/form/modules/tools/form-error-message/components/form-error-message/form-error-message.component";

@Component({
  selector: "sxw-addresses-autocomplete",
  templateUrl: "./addresses-autocomplete.component.html",
  styleUrls: ["./addresses-autocomplete.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressesAutocompleteComponent),
      multi: true,
    },
  ],
  imports: [TranslatePipe, FormsModule, FormErrorMessageComponent],
})
export class AddressesAutocompleteComponent extends CustomInput<string | IAddressValue> implements OnInit, OnChanges, OnDestroy {
  @Input() theme = "sxw-theme";
  @Input() placeholder = "enterlocation";
  @Input() override readonly = false;
  /**
   * Country Iso to enhance google places search
   */
  @Input() country?: { name?: string; short?: string };
  @Input() types: string[] = [];
  @Input() showClear = false;
  @Output("countryChange") onCountryChanged = new EventEmitter<{ name?: string; short?: string } | undefined>();
  @Output() onAddressChanged = new EventEmitter<google.maps.places.PlaceResult | undefined>(undefined);
  @Output() onInputFoucs = new EventEmitter<void>();

  protected addressValue = "";
  localizedAddress = false;

  @ViewChild("addressInput", { read: ElementRef }) addressInput!: ElementRef<HTMLInputElement>;

  protected addressAutoCompleteService = inject(AddressAutocompleteService);
  private ngZone = inject(NgZone);
  private rendrer2 = inject(Renderer2);
  private parent: any;
  override writeValue(value: string | IAddressValue) {
    this.addressValue = typeof value === "string" || !value ? value || "" : (value?.street ?? "");
    this.value = value;
  }

  override get summaryValue(): any {
    return this.addressValue || "";
  }

  async ngOnInit() {
    await this._initPlacesLib();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["country"]) {
      this.addAddressListener(() => this.ngZone.run(() => this.handleSelectedPlace()));
    }
    // remove listeners if readonly is true, attach listeners again if readonly is false, but note that readonly should not be firstChange,
    if (changes["readonly"]) {
      if (!!changes["readonly"].currentValue) {
        this.removeAddressListiner();
      }
      if (!changes["readonly"].currentValue && !changes["readonly"].firstChange) {
        // Using ngZone because address changeDetectorRef not update formControl Values in patch () successfully, even when
        // I use it in address-input.component
        this.addAddressListener(() => this.ngZone.run(() => this.handleSelectedPlace()));
      }
    }
  }

  protected async _initPlacesLib() {
    const isLibLoaded = await this.addressAutoCompleteService.initGooglePlacesLib();
    if (!isLibLoaded) {
      return;
    }
    // Using ngZone because address changeDetectorRef not update formControl Values in patch () successfully, even when
    // I use it in address-input.component
    this.addAddressListener(() =>
      this.ngZone.run(() => {
        this.handleSelectedPlace();
        this.onBlur();
      })
    );
  }

  handleSelectedPlace() {
    this.onTouched();
    const selectedPlace = this.addressAutoCompleteService?.place;
    const country = selectedPlace?.address_components?.find(address => address.types.includes("country"));
    this.onCountryChanged.emit({ name: country?.long_name, short: country?.short_name });
    this.value = {
      country: country?.long_name,
      street: this.addressValue ?? "",
      city: selectedPlace?.address_components?.find(address => address.types.includes("administrative_area_level_3"))?.long_name ?? "",
      building: selectedPlace?.address_components?.find(address => address.types.includes("street_number"))?.long_name ?? "",
      cap: selectedPlace?.address_components?.find(address => address.types.includes("postal_code"))?.long_name ?? "",
      region: selectedPlace?.address_components?.find(address => address.types.includes("administrative_area_level_1"))?.long_name ?? "",
      province: selectedPlace?.address_components?.find(address => address.types.includes("administrative_area_level_2"))?.long_name ?? "",
      lat: selectedPlace?.geometry?.location?.lat(),
      lng: selectedPlace?.geometry?.location?.lng(),
    };
    this.localizedAddress = true;
    this.onChange<string | IAddressValue>(this.value);
    this.addressValue = this.addressInput.nativeElement.value;
    this.onAddressChanged.emit({ ...selectedPlace, formatted_address: this.addressValue });
  }

  /**
   * Runs when address input changed
   * @param event
   */
  addressChanged(event?: Event) {
    this.addressValue = (event?.target as any)?.value ?? "";
    this.value = this.addressValue;
    this.localizedAddress = false;
    this.onChange<string | IAddressValue>(this.value);
    this.onTouched();
  }

  clearAddress() {
    this.addressChanged();
  }

  clearInput() {
    this.addressValue = "";
    if (this.addressInput.nativeElement) {
      this.addressInput.nativeElement.value = "";
    }
  }

  onBlur(e?: Event) {
    if (this.parent) this.rendrer2.setStyle(this.parent, "overflow-y", "auto");
  }

  onFocus(e: Event) {
    this.findStepContainer(e.target as any);
    if (this.parent) this.rendrer2.setStyle(this.parent, "overflow-y", "hidden");
    this.onInputFoucs.emit();
  }

  findStepContainer(input: HTMLInputElement) {
    this.parent = input.parentElement as any;
    while (this.parent && !this.parent?.classList.contains("content-container")) {
      this.parent = this.parent.parentElement;
    }
  }

  private addAddressListener(cb: (...args: any[]) => any) {
    this.addressAutoCompleteService.handlePlacesAutoComplete(this.country?.short ?? "", this.addressInput?.nativeElement, this.types, cb);
  }

  private removeAddressListiner() {
    this.addressAutoCompleteService.removePlaceAutoCompleteHandler();
    google.maps.event.clearInstanceListeners(this.addressInput.nativeElement);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.addressAutoCompleteService.removePlaceAutoCompleteHandler();
  }
}
